import React, { useMemo } from 'react';
import { useLocale } from '@hooks/useLocale';
import {
  MecProduct,
  isPrimaryProductImage,
} from '@adapters/bigcommerce/types/product';
import { slugify } from '@utils/url';
import Script from 'next/script';
import { getAvailabilitySchema, mapVariantToSchema } from './utils';
import { localizedProductsSlug } from '@utils/algolia/helpers';

export interface StructuredSearchDataProps {
  product?: MecProduct;
  productCode?: string;
  seoTitle?: string;
  seoDescription?: string;
  canonicalUrl?: string;
}

export interface StructuredBrandDataProps {
  brand: string;
}

export const SCHEMA_ORG = 'https://schema.org/';
export const MEC_URL = 'https://www.mec.ca/';
export const PRODUCT_IMG_UNAVAILABLE = 'productimgunavailable';
export const NO_COLOUR = 'NO_COLOUR';

export const StructuredBrandData = ({ brand }: StructuredBrandDataProps) => {
  const locale = useLocale();
  const productsSlug = localizedProductsSlug(locale).toLowerCase();
  const localizedBrands = locale === 'en' ? 'brands' : 'marques';
  const brandSlug = slugify(brand);
  const brandUrl = `${MEC_URL}${locale}/${productsSlug}/${localizedBrands}/${brandSlug}`;

  return (
    <div itemProp="brand" itemType={`${SCHEMA_ORG}Brand`} itemScope>
      <meta itemProp="name" content={brand} />
      <meta itemProp="url" content={brandUrl} />
    </div>
  );
};

export const StructuredSearchData = ({
  product,
  productCode,
  seoTitle,
  seoDescription,
  canonicalUrl,
}: StructuredSearchDataProps) => {
  const locale = useLocale();
  const productsSlug = localizedProductsSlug(locale).toLowerCase();
  const localizedBrands = locale === 'en' ? 'brands' : 'marques';
  const brandSlug = slugify(product?.brand?.name || '');
  const brandUrl = `${MEC_URL}${locale}/${productsSlug}/${localizedBrands}/${brandSlug}`;

  const name = seoTitle || '';

  const placeholderImage = `${MEC_URL}/images/${
    locale === 'en' ? 'EN' : 'FR'
  }-${PRODUCT_IMG_UNAVAILABLE}.svg`;

  const availabilitySchema = getAvailabilitySchema({
    availableForSaleOnline: product?.availableForSaleOnline || false,
    isInStock: product?.isInStock || false,
    isDiscontinued: product?.isDiscontinued || false,
  });

  const brand = product?.brand?.name || '';

  const colourVariants = product?.colours;
  const sizeVariants = product?.sizes;

  const productOutOfStock = !product?.isInStock;

  const variants = useMemo(
    () =>
      product?.variants?.map((variant) =>
        mapVariantToSchema({
          variant,
          product,
          canonicalUrl,
          colourVariants: product?.allColours,
          sizeVariants: product?.sizes,
          placeholderImage,
          productOutOfStock,
        })
      ),
    [product, canonicalUrl, placeholderImage, productOutOfStock]
  );

  const variesBy = [];
  if (sizeVariants?.length && sizeVariants?.length > 0) {
    variesBy.push(`${SCHEMA_ORG}size`);
  }

  if (
    colourVariants?.length &&
    colourVariants?.length > 0 &&
    colourVariants[0].name !== NO_COLOUR
  ) {
    variesBy.push(`${SCHEMA_ORG}color`);
  }

  const defaultImage = product?.images.find(
    (image) =>
      isPrimaryProductImage(image) &&
      !image.urlOriginal.includes(PRODUCT_IMG_UNAVAILABLE)
  );

  const jsonLd = {
    '@context': SCHEMA_ORG,
    '@type': 'ProductGroup',
    '@id': canonicalUrl,
    productGroupId: productCode,
    name,
    image: defaultImage?.urlOriginal || placeholderImage,
    description: seoDescription,
    url: canonicalUrl,
    sku: productCode,
    brand: {
      '@type': 'Brand',
      name: brand,
      url: brandUrl,
    },
    offers: {
      '@type': 'Offer',
      availability: availabilitySchema,
    },
    variesBy,
    hasVariant: variants,
  };

  return (
    <Script
      id="product-structured-search-data"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};
